import Locales, { Translations } from "../menu/locales"
import React, { ReactNode, useState } from "react"
import ReactMarkdown, { defaultUrlTransform } from "react-markdown"

import { A } from "../../utils/router"
import ConditionalWrapper from "../miscellaneous/conditional-wrapper"
import FooterStyles from "./footer-v2.module.css"
import { LazyImage } from "../universal/image"
import Stars from "../universal/stars"
import classNames from "classnames"
import { useAuthState } from "../../context/auth"
import { useCCMLoaded } from "../../hooks/use-ccm-loaded"
import { useLocalesState } from "../../context/locales"
import { useQueryTrustedShops } from "../../hooks/use-query-trusted-shops"
import { useTranslation } from "react-i18next"

type FooterProps = {
    configuration: {
        language?: boolean
        logoLink?: string
    }
    menu?: ReactNode
    sitemap?: ReactNode
    translations?: Translations
}

const Footer = ({ configuration, menu, sitemap, translations }: FooterProps) => {
    const { t } = useTranslation("shared")
    const { isPartner } = useAuthState()
    const { region, language } = useLocalesState()

    const ccmLoaded = useCCMLoaded()

    const [contactDetails, setContactDetails] = useState(false)

    const { data: trustedShopsData } = useQueryTrustedShops()

    const rating = trustedShopsData?.grades["365days"].rating

    return (
        <footer className={FooterStyles.footer}>
            <div className={FooterStyles.container}>
                <div className={FooterStyles.logoWrapper}>
                    <ConditionalWrapper
                        condition={!!configuration.logoLink}
                        wrapper={[
                            children => (
                                <A href={configuration.logoLink} className={FooterStyles.logo}>
                                    {children}
                                </A>
                            ),
                            children => <div className={FooterStyles.logo}>{children}</div>,
                        ]}
                    >
                        <LazyImage
                            url="assets"
                            path="/logo/LaVita_Logo_2020.png"
                            srcSet={[{ w: 76 }, { w: 90, default: true }, { w: 152 }, { w: 180 }]}
                            sizes="(max-width: 1365px) 76px, 90px"
                            alt="LaVita Logo"
                        />
                    </ConditionalWrapper>
                </div>
                <div className={FooterStyles.menu}>{menu}</div>
                {region !== "TR" && (
                    <div className={FooterStyles.contact}>
                        <button
                            type="button"
                            onClick={() => setContactDetails(contactDetails => !contactDetails)}
                            className={classNames(FooterStyles.contactButton, {
                                [FooterStyles.contactButtonActive]: contactDetails,
                            })}
                        >
                            <span>{t("footer_info_consulting_headline")}</span>
                            <i className={classNames("lavita-icon", FooterStyles.contactButtonIcon)}></i>
                        </button>
                        <div className={FooterStyles.contactDetails}>
                            <div>
                                <div>
                                    <ReactMarkdown
                                        urlTransform={url => (url.startsWith("tel:") ? url : defaultUrlTransform(url))}
                                    >
                                        {t(
                                            isPartner && region !== "CH"
                                                ? "footer_info_consulting_partner"
                                                : "footer_info_consulting"
                                        )}
                                    </ReactMarkdown>
                                </div>
                                <div className={FooterStyles.contactDivider} />
                                <div>
                                    <ReactMarkdown>{t("footer_info_contact")}</ReactMarkdown>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
                <div className={FooterStyles.info}>
                    {rating && language === "de" && region === "DE" && !!process.env.TRUSTED_SHOPS_ID && (
                        <div className={FooterStyles.trustedShop}>
                            <p>
                                <strong>{t("footer_label_trusted_shop")}</strong>
                            </p>
                            <div className={FooterStyles.trustedShopBadgeWrapper}>
                                <a
                                    href="https://www.trustedshops.de/bewertung/info_X49E710FC236D0FD6604F8259314C8A7F.html"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className={FooterStyles.trustedShopBadge}
                                >
                                    <div className={FooterStyles.trustedShopLogo}>
                                        <LazyImage
                                            url="assets"
                                            path="/lavita.de/trust/trustmark.svg"
                                            alt="Trusted Shops"
                                        />
                                    </div>
                                    <div>
                                        <div className={FooterStyles.trustedShopProtection}>
                                            {t("truststripe_buyer_protection")}
                                        </div>
                                        <div className={FooterStyles.trustedShopRatingWrapper}>
                                            <Stars rating={rating} />
                                            <div className={FooterStyles.trustedShopRating}>
                                                {rating.toLocaleString(language)}
                                            </div>
                                            <div className={FooterStyles.trustedShopRatingText}>
                                                {t(
                                                    rating < 1.5
                                                        ? "truststripe_very_poor"
                                                        : rating < 2.5
                                                        ? "truststripe_poor"
                                                        : rating < 3.5
                                                        ? "truststripe_fair"
                                                        : rating < 4.5
                                                        ? "truststripe_good"
                                                        : "truststripe_excellent"
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </a>
                            </div>
                        </div>
                    )}
                    {["DE", "AT", "CH"].includes(region) && language === "de" && !isPartner && (
                        <div className={FooterStyles.appWrapper}>
                            <div className={FooterStyles.app}>
                                <p>
                                    <strong>{t("footer_label_app")}</strong>
                                </p>
                                <div>
                                    <a
                                        href="https://play.google.com/store/apps/details?id=de.lavita.lavitaapp"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <LazyImage
                                            url="assets"
                                            path="/lavita.de/app/google-play-badge-ai.png"
                                            alt="Google Play"
                                        />
                                    </a>
                                    <a
                                        href="https://apps.apple.com/de/app/lavita/id1484793998"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <LazyImage
                                            url="assets"
                                            path="/lavita.de/app/3327417.svg"
                                            alt="Apple App Store"
                                        />
                                    </a>
                                </div>
                            </div>
                        </div>
                    )}
                    <div className={FooterStyles.socialWrapper}>
                        <div className={FooterStyles.social}>
                            <p>
                                <strong>{t("footer_label_social")}</strong>
                            </p>
                            <div>
                                <a href={t("footer_social_fb")} aria-label={t("footer_facebook")}>
                                    <i className="lavita-icon"></i>
                                </a>
                                <a href={t("footer_social_instagram")} aria-label={t("footer_instagram")}>
                                    <i className="lavita-icon"></i>
                                </a>
                                <a href={t("footer_social_youtube")} aria-label={t("footer_youtube")}>
                                    <i className="lavita-icon"></i>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className={FooterStyles.divider} />
            <div className={FooterStyles.container}>
                <div className={classNames(FooterStyles.links, {[FooterStyles.fullWidth]: !configuration.language})}>
                    {region !== "TR" && (
                        <A href={`${process.env.WEB_URL}/${t("footer_legalnotice_path")}`}>{t("footer_legalnotice")}</A>
                    )}
                    {region === "TR" && (
                        <A href={`${process.env.WEB_URL}/${t("footer_cookie_path")}`}>{t("footer_cookie")}</A>
                    )}
                    <A href={`${process.env.WEB_URL}/${t("footer_dataprotection_path")}`}>
                        {t("footer_dataprotection")}
                    </A>
                    {region !== "TR" && (
                        <>
                            <A href={`${process.env.WEB_URL}/${t("footer_terms_and_conditions_path")}`}>
                                {t("footer_terms_and_conditions")}
                            </A>
                            {ccmLoaded && (
                                <button onClick={() => window.CCM.openControlPanel()}>{t("footer_cookie")}</button>
                            )}
                            <A href={`${process.env.WEB_URL}/${t("footer_revocation_path")}`}>
                                {t("footer_revocation")}
                            </A>
                            {region !== "CZ" && region !== "CH" && (
                                <A
                                    className={FooterStyles.button}
                                    href={`${process.env.WEB_URL}/${t("footer_cancel_contract_path")}`}
                                >
                                    {t("footer_cancel_contract")}
                                </A>
                            )}
                        </>
                    )}
                    {region === "TR" && (
                        <A href={`${process.env.WEB_URL}/${t("footer_distance_contract_path")}`}>
                            {t("footer_distance_contract")}
                        </A>
                    )}
                    {region === "DE" && <A href={t("footer_whistleblower_url")}>{t("footer_whistleblower")}</A>}
                    {region !== "CH" && region !== "TR" && region !== "CZ" && (
                        <A href={`${process.env.WEB_URL}/${t("footer_accessibility_path")}`}>
                            {t("footer_accessibility")}
                        </A>
                    )}
                    <A href={`${process.env.WEB_URL}/${t("footer_contact_path")}`}>{t("footer_contact")}</A>
                    {sitemap}
                </div>
                {configuration.language && (
                    <div className={FooterStyles.locales}>
                        <Locales type="footer" translations={translations} />
                    </div>
                )}
            </div>
        </footer>
    )
}

export default Footer
